import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import WPPagination from "../../packages/WPPagination";
import { useToastNotification } from "../../packages/WPToastNotification/WPToastNotificationProvider";

import useLicenseManagementData from "../../hooks/useLicenseManagementData";

import Header from "../../components/Header";
import LicenseAppliedFilters from "./LicenseManagementHeader/LicenseAppliedFilters";
import LicenseManagementHeader from "./LicenseManagementHeader";
import LicenseManagementTable from "./LicenseManagementTable";

import { PAGE_SIZE_OPTIONS } from "../../constants/global.constants";
import { HIDDEN_LICENSE_IDS } from "../../constants/LicenseManagement.constants";

import CircularLoader from "../../common/styles/components/circularLoader";

import { downloadBlobData } from "../../utils/Utils";

import { API_URLS } from "../../network/appUrls";
import { exportExcel } from "../../network/cipAPI";

import "./LicenseManagement.scss";

const LicenseManagement = () => {
  const { t } = useTranslation();
  const { showSuccessNotification, showErrorNotification } =
    useToastNotification();
  const {
    userRoleLicenseData,
    licensePermissions: rawLicensePermissions,
    licensePayload,
    updateLicensePayload,
    isLoading,
  } = useLicenseManagementData();
  const { userRoleLicenses = [], totalCount = 0 } = userRoleLicenseData || {};
  const currentPage = licensePayload.pageNumber || 1;
  const pageSize = licensePayload.pageSize || PAGE_SIZE_OPTIONS[1];

  const [usersLicenseData, setUsersLicenseData] = useState(userRoleLicenses);
  const [isExportLoading, setIsExportLoading] = useState(false);

  // Filter out default licenses that should not be shown in License Management views.
  const licensePermissions = rawLicensePermissions.filter(
    (license) => !HIDDEN_LICENSE_IDS.includes(Number(license?.id)),
  );

  /**
   * Unified handler for all filter operations.
   * Accepts filter updates and resets page to 1.
   * @param {Object} filterUpdates - Object containing filter fields to update
   */
  const handleFilterUpdate = useCallback(
    (filterUpdates) => {
      updateLicensePayload({ ...filterUpdates, pageNumber: 1 });
    },
    [updateLicensePayload],
  );

  const handleUserLicenseUpdate = useCallback((userId, updatedLicense) => {
    if (!userId || !updatedLicense) {
      return;
    }

    setUsersLicenseData((prevData) =>
      (prevData || []).map((user) => {
        if (user.userId !== userId) {
          return user;
        }

        const existingLicenses = Array.isArray(user.licenses)
          ? user.licenses
          : [];
        const existingIdx = existingLicenses.findIndex(
          (license) => license?.id === updatedLicense?.id,
        );

        const nextLicenses = [...existingLicenses];
        if (existingIdx >= 0) {
          nextLicenses[existingIdx] = {
            ...nextLicenses[existingIdx],
            ...updatedLicense,
          };
        } else {
          nextLicenses.push(updatedLicense);
        }

        return {
          ...user,
          licenses: nextLicenses,
        };
      }),
    );
  }, []);

  const handlePageChange = useCallback(
    (page, newPageSize) => {
      if (page === currentPage && (!newPageSize || newPageSize === pageSize))
        return;
      if (newPageSize && newPageSize !== pageSize) {
        updateLicensePayload({ pageNumber: 1, pageSize: newPageSize });
      } else {
        updateLicensePayload({ pageNumber: page, pageSize });
      }
    },
    [currentPage, pageSize, updateLicensePayload],
  );

  const handleExportUserLicenses = async () => {
    setIsExportLoading(true);
    try {
      const response = await exportExcel(API_URLS.license);

      if (response?.data) {
        downloadBlobData(
          response.data,
          t("licenseManagement.licensesFileName"),
          response.headers,
        );
        showSuccessNotification(
          t("notificationStrings.tableDataExportSuccess"),
        );
      }
    } catch (error) {
      showErrorNotification(t("notificationStrings.tableDataExportFailed"));
    } finally {
      setIsExportLoading(false);
    }
  };

  /**
   * Add/remove body class to restrict overflow only on License Management page
   * This prevents scrollbars and page shakiness when tooltips or overlays are shown
   */
  useEffect(() => {
    document.body.classList.add("license-management-body");
    return () => {
      document.body.classList.remove("license-management-body");
    };
  }, []);

  useEffect(() => {
    setUsersLicenseData(userRoleLicenses);
  }, [userRoleLicenses]);

  return (
    <div className="container-main">
      <Header />
      <div className="main-container">
        <div className="content-container">
          <div className="license-management-main">
            <LicenseManagementHeader
              updateLicensePayload={updateLicensePayload}
              licensePayload={licensePayload}
              onUserLicenseUpdate={handleUserLicenseUpdate}
              onExportUserLicenses={handleExportUserLicenses}
            />
            <LicenseAppliedFilters
              licensePayload={licensePayload}
              licensePermissions={licensePermissions}
              onFilterUpdate={handleFilterUpdate}
            />
            <div className="license-management-container">
              <LicenseManagementTable
                rowData={usersLicenseData}
                licenseData={licensePermissions}
                licensePayload={licensePayload}
                updateLicensePayload={updateLicensePayload}
                showSubheaderFilters={true}
                onUserLicenseUpdate={handleUserLicenseUpdate}
              />
            </div>
          </div>
        </div>
        <div className="license-management-pagination-footer">
          <WPPagination
            totalItems={totalCount}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
      {(isLoading || isExportLoading) && (
        <div className="circular-loader-overlay">
          <CircularLoader />
        </div>
      )}
    </div>
  );
};

export default LicenseManagement;
